import { PREVIEW_IMAGE_PAINTFILL_COLOR_PLACEMENTS } from '../constants';

export default {
    convertTransform(value) {
        const prefix = 'matrix3d(';

        if (value.startsWith(prefix)) {
            return value.substr(prefix.length, value.length - prefix.length - 1);
        }

        return value;
    },

    // eslint-disable-next-line no-unused-vars
    getPreviewImageUrl(controller, useView) {
        const getColor = (c) => {
            const hex = c.custom['v-paintfill'] ?? c.custom['v-hex'];

            return hex.startsWith('#') ? hex.substr(1) : hex;
        };

        let withColors = !!this.uiPaintfillRawPreviewActive;

        // Check if there is even one color selected.
        const paintfillPlacements = [
            'PutterFrontPaintfill',
            'PutterTopPaintfill',
            'PutterBackPaintfill',
        ];

        // eslint-disable-next-line no-restricted-syntax
        for (const placement of paintfillPlacements) {
            const selected = controller.selectedAtPlacement(placement);
            if (selected && !selected.placeholder) {
                withColors = true;
                break;
            }
        }

        // eslint-disable-next-line no-restricted-syntax
        for (const placement of Object.values(PREVIEW_IMAGE_PAINTFILL_COLOR_PLACEMENTS).flat()) {
            const selected = controller.selectedAtPlacement(placement);
            if (selected && !selected.placeholder && selected.custom['v-hex']) {
                withColors = true;
                break;
            }
        }

        const viewCode = useView ?? this.activeView;

        const view = controller.blueprint.views.find((x) => x.code === viewCode);

        const parent = controller.selectedAtPlacement('PutterParent');
        let parentCode = null;
        if (parent != null) {
            parentCode = `CZ-${parent.placementIndependentCode.replace(':', '-')}`;
        }

        let url = controller.makeFrameUrl(view, controller.state, view.frameLow, {});

        if (withColors) {
            url = url.replace(parentCode, `${parentCode}-Blank`);
        }

        // Get stamp.
        const stamp = controller.selectedAtPlacement('Stamp');

        // eslint-disable-next-line no-unused-vars
        const sso = viewCode === 'Sole' ? 10 : 5;
        // eslint-disable-next-line no-unused-vars
        const sto = viewCode === 'Sole' ? 15 : 5;

        const stampTextShading = ',isdw(x:1;y:1;b:3;c:000000;o:1),glw(x:1;y:1;b:1;c:ffffff;o:1)';

        // Render the stamp.
        if (stamp && !stamp.placeholder) {
            const stampCode = stamp.placementIndependentCode;

            let stampColor = 'ffffff';

            const colorComponent = controller.selectedAtPlacement('StampColor');
            if (colorComponent && !colorComponent.placeholder) {
                stampColor = (colorComponent.custom['v-paintfill'] ?? colorComponent.custom['v-hex'] ?? 'ffffff').replace('#', '');
            } else {
                stampColor = '000000b0';
            }

            // Find the joint.
            const component = controller.selectedAtPlacement('PutterParent');
            if (component) {
                const joint = component.joints.find((x) => x.code === 'Stamp');

                if (joint) {
                    const viewSettings = joint?.views?.[viewCode];

                    if (viewSettings?.hidden === 'true') {
                        // Do not render.
                    } else if (viewSettings?.['anchor-transform']) {
                        url += '&l&g=Stamp&gsz=1000,1000&src=BOX1000X10000C00000000';
                        // eslint-disable-next-line max-len
                        url += `&l&g=Stamp&gsz=1000,1000&src=${stampCode}`;

                        // Render color (v-paintfill + v-render-transparency) and effects (shrp, dlit, expo) as tuned in ColorFit.
                        const stampRender = this.getColorRenderParams(colorComponent, stampColor);

                        stampColor = this.makeColor(this.multiplyColor(this.parseColor(stampRender.color), 0.85));

                        // eslint-disable-next-line max-len
                        url += `&l&src=g!Stamp&matrix3d=${this.convertTransform(viewSettings['anchor-transform'])}&fx=colo(c:00000000),cadd(c:${stampColor}),${stampRender.fx}${stampTextShading}`;
                    }
                }
            }
        }

        const textColorComponent = controller.selectedAtPlacement('StampingColorsLine1');
        const initialsComponent = controller.selectedAtPlacement('StampingStyle');

        // Render the text.
        if (initialsComponent && !initialsComponent.placeholder) {
            let text = initialsComponent.custom['v-stamping-text-1'];

            // Better empty default.
            let textColor = '000000a0';

            if (textColorComponent && !textColorComponent.placeholder) {
                textColor = (textColorComponent.custom['v-paintfill'] ?? textColorComponent.custom['v-hex'] ?? textColor).replace('#', '');
            }

            let tracking = 350;

            if (this.getDotsStyle(initialsComponent.code)) {
                if (text?.length > 0) {
                    text = `${text.split('').join('.')}.`;
                }

                tracking = 100;
            }

            let dancing = '';

            if (this.getDancingStyle(initialsComponent.code) === 'dancing') {
                dancing = 'd:1;';

                const noDots = text.replace(/\./g, '');

                if (noDots.length === 3) {
                    dancing += 'di:-15;';
                } else {
                    dancing += 'di:15;';
                }

                dancing += 'ds:30;dmin:-15;dmax:15;';
            }

            if (text) {
                // Find the joint.
                const component = controller.selectedAtPlacement('PutterParent');
                if (component) {
                    const joint = component.joints.find((x) => x.code === 'Personalization');

                    if (joint) {
                        const viewSettings = joint?.views?.[viewCode];

                        if (viewSettings?.hidden === 'true') {
                            // Do not render.
                        } else if (viewSettings?.['anchor-transform']) {
                            url += '&l&g=Personalization&gsz=2000,500&src=BOX2000X10000C00000000';
                            // eslint-disable-next-line max-len
                            url += `&l&g=Personalization&txt=${text}&fnt=f:Font-OpenSans_Condensed-ExtraBold.ttf;s:300;tr:${tracking};${dancing}a:c;va:c;&sz=2000,500`;

                            // Render color (v-paintfill + v-render-transparency) and effects (shrp, dlit, expo) as tuned in ColorFit.
                            const textRender = this.getColorRenderParams(textColorComponent, textColor);

                            textColor = this.makeColor(this.multiplyColor(this.parseColor(textRender.color), 0.85));

                            // eslint-disable-next-line max-len
                            url += `&l&src=g!Personalization&matrix3d=${this.convertTransform(viewSettings['anchor-transform'])}&fx=colo(c:00000000),cadd(c:${textColor}),${textRender.fx}${stampTextShading}`;
                        }
                    }
                }
            }
        }

        // Render the component.
        if (parentCode) {
            const addColor = (placement) => {
                // Don't paint if there is no associated paintfill.
                const havePlacement = controller.findPlacement(placement);
                if (!havePlacement) {
                    return;
                }

                const selected = controller.selectedAtPlacement(placement);

                if (selected && !selected.placeholder && selected.custom['v-hex']) {
                    const hex = getColor(selected);

                    // Render color (v-paintfill + v-render-transparency) and effects (shrp, dlit, expo) as tuned in ColorFit.
                    const { color, fx } = this.getColorRenderParams(selected, hex);

                    url += `&l&src=${parentCode}-${placement}-${view.frameLow}&fx=colo(c:${color}),${fx}`;
                } else if (withColors) {
                    // Draw default uncolored.
                    url += `&l&src=${parentCode}-${placement}-${view.frameLow}`;
                }
            };

            Object.values(PREVIEW_IMAGE_PAINTFILL_COLOR_PLACEMENTS)
                .flat()
                .forEach(addColor);
        }

        url += '&fmt=webp&qlt=75';

        return url;
    },

    // Rendering parameters of a stamping color option, as tuned in the ColorFit test page.
    // The render color is `baseColor` (v-paintfill, or v-hex when missing) with the option's
    // v-render-transparency appended. Effects are sharpen (v-shrp-size / v-shrp-strength), then
    // remove-lighting (v-dlit-size / v-dlit-strength), shared by every color of the same type
    // (v-translucent), then the color's own exposure (v-expo). Missing attributes take the
    // per-type defaults. Without a color option the base color is returned untouched.
    getColorRenderParams(colorComponent, baseColor) {
        const custom = colorComponent?.custom ?? {};
        const translucent = custom['v-translucent'] === 'true';

        const defaults = translucent
            ? {
                'v-render-transparency': 'F0',
                'v-shrp-size': '0',
                'v-shrp-strength': '0',
                'v-dlit-size': '1',
                'v-dlit-strength': '0',
                'v-expo': '0',
            }
            : {
                'v-render-transparency': 'FA',
                'v-shrp-size': '2',
                'v-shrp-strength': '-0.5',
                'v-dlit-size': '1',
                'v-dlit-strength': '1',
                'v-expo': '0',
            };

        const param = (key) => {
            const value = custom[key];

            if (value == null || String(value).trim() === '') {
                return defaults[key];
            }

            return String(value).trim();
        };

        let color = baseColor;

        if (colorComponent && !colorComponent.placeholder) {
            color = `${baseColor.substr(0, 6)}${param('v-render-transparency')}`;
        }

        return {
            color,
            translucent,
            // eslint-disable-next-line max-len
            fx: `shrp(s:${param('v-shrp-size')};a:${param('v-shrp-strength')}),dlit(s:${param('v-dlit-size')};a:${param('v-dlit-strength')}),expo(s:${param('v-expo')})`,
        };
    },

    adjustSaturation({
        r,
        g,
        b,
        a = 1,
    }, sFactor) {
        const max = Math.max(r, g, b);
        const min = Math.min(r, g, b);

        if (max === min) {
            return {
                r,
                g,
                b,
                a,
            };
        }

        const saturation = (max - min) / max;
        const newSaturation = Math.min(1, Math.max(0, saturation * sFactor));
        const ratio = newSaturation / saturation;
        const channel = (value) => max - ((max - value) * ratio);

        return {
            r: channel(r),
            g: channel(g),
            b: channel(b),
            a,
        };
    },

    makeVector(r, g, b, a = 1) {
        return {
            r,
            g,
            b,
            a,
        };
    },

    multiplyColor({
        r,
        g,
        b,
        a,
    }, f) {
        return {
            r: r * f,
            g: g * f,
            b: b * f,
            a,
        };
    },

    makeColor({
        r,
        g,
        b,
        a = 1,
    }) {
        const part = (value) => {
            const clamped = Math.min(1, Math.max(0, value));

            return Math.round(clamped * 255).toString(16).padStart(2, '0');
        };

        const hex = `${part(r)}${part(g)}${part(b)}`;
        const alpha = part(a);

        if (alpha === 'ff') {
            return hex;
        }

        return `${hex}${alpha}`;
    },

    parseColor(hex) {
        const part = (i) => parseInt(hex.slice(i, i + 2), 16) / 255;

        return {
            r: part(0),
            g: part(2),
            b: part(4),
            a: hex.length === 8 ? part(6) : 1,
        };
    },
};
